// frontend/src/Hooks/useHandleRedirect.js
// Minimal redirect hook: navigate inside the app with react-router,
// optionally update Redux route state, and use full redirects only
// for external or special links (mailto/tel).

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

// Fixed-navbar offset so anchored sections aren't hidden under the header.
const NAV_OFFSET = 96;

// Smooth-scroll to an element id, retrying briefly so lazy-mounted sections
// (e.g. the contact form below the fold) are found once they render.
export function scrollToHashId(id, retries = 12) {
  if (!id || typeof document === 'undefined') return false;
  const el = document.getElementById(id);
  if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top: y, behavior: 'smooth' });
    return true;
  }
  if (retries > 0) setTimeout(() => scrollToHashId(id, retries - 1), 150);
  return false;
}

/**
 * useHandleRedirect
 * @param {Object} options
 * @param {(path: string) => void} [options.onReduxRedirect] - optional callback to update route state (e.g. dispatch(authActions.redirectScreen(path)))
 * @returns {(evtOrPath: MouseEvent | string, explicitPath?: string) => void}
 */
export default function useHandleRedirect(options = {}) {
  const { onReduxRedirect } = options;
  const navigate = useNavigate();

  return React.useCallback((evtOrPath, explicitPath) => {
    // Resolve path from either a string or an event/element
    let path = '';
    if (typeof evtOrPath === 'string') {
      path = evtOrPath;
    } else {
      evtOrPath?.preventDefault?.();
      path =
        explicitPath ||
        evtOrPath?.currentTarget?.value ||
        evtOrPath?.currentTarget?.getAttribute?.('href') ||
        '/landing';
    }

    const isExternal = /^https?:\/\//i.test(path);
    const isSpecial = /^(mailto:|tel:)/i.test(path);

    // External or special links -> let the browser handle a full redirect
    // console.log('[useHandleRedirect] Navigating to:', path);

    if (isExternal || isSpecial) {
      window.location.assign(path);
      return;
    }

    // In-page anchors ("#contact", "/#contact", "/path#section"): react-router's
    // navigate() changes the URL but never scrolls to the hash, so handle it here.
    const hashMatch = path.match(/#([\w-]+)$/);
    if (hashMatch) {
      const id = hashMatch[1];
      const targetPath = path.split('#')[0] || '/';
      const here =
        window.location.pathname === targetPath ||
        (targetPath === '/' && (window.location.pathname === '/' || window.location.pathname === '/landing'));

      if (here || document.getElementById(id)) {
        // Already on the right page (or the target exists) — just scroll.
        scrollToHashId(id);
        if (window.history?.replaceState) window.history.replaceState(null, '', path);
      } else {
        // Different page — navigate first, then scroll once it renders.
        navigate(targetPath);
        setTimeout(() => scrollToHashId(id), 250);
      }
      return;
    }

    // Optional route-state update (Redux, etc.) supplied by caller
    if (typeof onReduxRedirect === 'function') {
      try {
        onReduxRedirect(path);
      } catch (_) {
        // no-op
      }
    }

    // Simple SPA navigation
    navigate(path);
  }, [navigate, onReduxRedirect]);
}